import { useMemo, type ReactNode } from 'react';
import { useTranslation } from './useTranslation';
import { TranslationKeyEnum } from '../types/constants';
import { usePetParentReportActions } from './usePetParentReports';
import { trackEvent } from '../services/analytics';

/**
 * Consent copy shown under pet-parent modal titles.
 * Canadian shelters also get a Learn more link to the opt-in guide.
 */
export const useCanadianConsentSubtitle = (isCanadian: boolean): ReactNode => {
  const { t } = useTranslation();
  const { downloadOptinGuide } = usePetParentReportActions();

  return useMemo(() => {
    const subText = t(TranslationKeyEnum.PetParentSubmissionMethodModalSubText);
    const learnMoreText = t(TranslationKeyEnum.PetParentSubmissionMethodModalLearnMore);
    const hasLearnMore =
      isCanadian &&
      Boolean(learnMoreText) &&
      learnMoreText !== TranslationKeyEnum.PetParentSubmissionMethodModalLearnMore;

    if (!hasLearnMore) {
      return subText;
    }

    const handleOptInGuideClick = () => {
      trackEvent({
        action: 'opt-in_guide',
        category: 'pet_parent_report',
        label: learnMoreText,
      });
      downloadOptinGuide();
    };

    return (
      <>
        {subText}{' '}
        <button
          type="button"
          onClick={handleOptInGuideClick}
          className="underline hover:opacity-80 transition-opacity inline font-normal text-black-600 bg-transparent border-none cursor-pointer p-0"
        >
          {learnMoreText}
        </button>
      </>
    );
  }, [t, isCanadian, downloadOptinGuide]);
};
